// Timeline completo de eventos de engagement - FASE 2

import React from 'react';
import { 
  Eye, Video, Image, FileText, MousePointer, 
  DollarSign, Lock, CheckCircle, XCircle, Clock, Mail 
} from 'lucide-react';
import { formatTimeAgo, formatFullDateTime, getTimeAgoColor } from '../../../utils/engagementTimeFormatters';

/**
 * Timeline completo de eventos
 * Muestra todos los eventos agrupados por sesión/visita
 */
export const EventTimeline = ({ events }) => {
  if (!events || events.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        No hay eventos registrados
      </div>
    );
  }

  // Ordenar eventos cronológicamente (más reciente primero)
  const sortedEvents = [...events].sort((a, b) => 
    new Date(b.created_at) - new Date(a.created_at)
  );

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto">
      {sortedEvents.map((event, index) => (
        <EventItem key={index} event={event} />
      ))}
    </div>
  );
};

/**
 * Item individual de evento
 */
const EventItem = ({ event }) => {
  const config = getEventConfig(event.event_type);
  const Icon = config.icon;

  return (
    <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-all">
      {/* Icono */}
      <div className={`${config.bg} p-2 rounded-lg flex-shrink-0`}>
        <Icon className={`w-4 h-4 ${config.color}`} />
      </div>

      {/* Contenido */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <div className="text-white font-medium">{config.label}</div>
            {event.event_data && Object.keys(event.event_data).length > 0 && (
              <div className="text-sm text-slate-400 mt-1">
                {formatEventData(event.event_type, event.event_data)}
              </div>
            )}
          </div>
          <div className={`text-xs flex-shrink-0 ${getTimeAgoColor(event.created_at)}`}>
            {formatTimeAgo(event.created_at)}
          </div>
        </div>
        <div className="text-xs text-slate-500 mt-1">
          {formatFullDateTime(event.created_at)}
        </div>
      </div>
    </div>
  );
};

/**
 * Configuración visual por tipo de evento
 */
const getEventConfig = (eventType) => {
  const configs = {
    // Navegación
    'page_view': {
      icon: Eye,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      label: 'Visitó la página',
    },
    'page_return': {
      icon: Eye,
      color: 'text-green-400',
      bg: 'bg-green-500/10',
      label: 'Volvió a la página',
    },
    // Contenido - Video
    'video_view_start': {
      icon: Video,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      label: 'Vio el video',
    },
    // ✅ NUEVO: Eventos del plano
    'floorplan_view_start': {
      icon: FileText,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10',
      label: 'Vio el plano',
    },
    'floorplan_view_duration': {
      icon: Clock,
      color: 'text-slate-400',
      bg: 'bg-slate-700/30',
      label: 'Tiempo viendo plano',
    },
    'tab_view': {
      icon: Eye,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      label: 'Vio pestaña',
    },
    'document_view_start': {
      icon: FileText,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      label: 'Abrió documento',
    },
    'document_view_end': {
      icon: FileText,
      color: 'text-slate-400',
      bg: 'bg-slate-700/30',
      label: 'Cerró documento',
    },
    'photo_view_start': {
      icon: Image,
      color: 'text-amber-400',
      bg: 'bg-amber-500/10',
      label: 'Abrió galería de fotos',
    },
    'photo_session_summary': {
      icon: Image,
      color: 'text-amber-400',
      bg: 'bg-amber-500/10',
      label: 'Resumen de fotos',
    },
    'description_read': {
      icon: FileText,
      color: 'text-green-400',
      bg: 'bg-green-500/10',
      label: '✅ Leyó la descripción',
    },

    // Formulario de puja
    'bid_form_focus': {
      icon: MousePointer,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      label: 'Enfocó formulario de puja',
    },
    'bid_amount_changed': {
      icon: DollarSign,
      color: 'text-amber-400',
      bg: 'bg-amber-500/10',
      label: 'Cambió cantidad',
    },
    'bid_conditions_interact': {
      icon: CheckCircle,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      label: 'Modificó condiciones',
    },
    'bid_custom_conditions_written': {
      icon: FileText,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      label: 'Escribió condiciones personalizadas',
    },
    'bid_submit_attempt_failed': {
      icon: XCircle,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      label: '❌ Intento de puja fallido',
    },
    'bid_submit_success': {
      icon: CheckCircle,
      color: 'text-green-400',
      bg: 'bg-green-500/10',
      label: '✅ Puja validada',
    },

    // Confirmación
    'bid_confirm_modal_opened': {
      icon: CheckCircle,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      label: 'Abrió modal de confirmación',
    },
    'bid_confirm_amount_mismatch': {
      icon: XCircle,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      label: '❌ Cantidad no coincide',
    },
    'bid_confirm_modal_abandoned': {
      icon: XCircle,
      color: 'text-amber-400',
      bg: 'bg-amber-500/10',
      label: 'Abandonó confirmación',
    },

    // Verificación por email
    'bid_verification_email_sent': {
      icon: Mail,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      label: '📧 Email de verificación enviado',
    },
    'bid_verified': {
      icon: CheckCircle,
      color: 'text-green-400',
      bg: 'bg-green-500/10',
      label: '🎉 Oferta verificada y confirmada',
    },
    'bid_intent_expired': {
      icon: Clock,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      label: '⏰ Verificación expirada',
    },

    // Completado Legacy (para históricos)
    'bid_completed': {
      icon: CheckCircle,
      color: 'text-green-400',
      bg: 'bg-green-500/10',
      label: '🎉 Oferta completada',
    },
    'bid_creation_error': {
      icon: XCircle,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      label: '❌ Error técnico al crear oferta',
    },
  };

  return configs[eventType] || {
    icon: Clock,
    color: 'text-slate-400',
    bg: 'bg-slate-700/30',
    label: eventType,
  };
};

/**
 * Formatear datos del evento de forma amigable
 */
const formatEventData = (eventType, data) => {
  switch (eventType) {
    case 'bid_amount_changed':
      return `€${data.amount?.toLocaleString()} (cambio #${data.changeCount})`;

    case 'bid_custom_conditions_written':
      return `${data.textLength} caracteres`;

    case 'bid_submit_attempt_failed':
      return `Error: ${data.errorType}`;

    case 'photo_session_summary':
      return `${data.photosViewed}/${data.totalPhotos} fotos vistas, ${data.navigations} navegaciones, ${data.durationSeconds}s`;

    case 'tab_view':
      return data.tabName || null;

    case 'document_view_start':
      return data.documentName || null;

    case 'document_view_end':
      return data.documentName ? `${data.documentName}${data.viewDurationSeconds ? ` · ${data.viewDurationSeconds}s` : ''}` : null;

    case 'floorplan_view_duration':
      return `${data.durationSeconds}s`;

    case 'bid_completed':
      return `€${data.amount?.toLocaleString()} - ${data.totalTimeFromFormFocus}s desde inicio`;

    case 'bid_verification_email_sent':
      return `€${data.amount?.toLocaleString()} - Pendiente de verificar`;

    case 'bid_verified':
      return `€${data.amount?.toLocaleString()} - ✅ Confirmada`;

    case 'bid_intent_expired':
      return `Enlace no verificado a tiempo`;

    default:
      return null;
  }
};

export default EventTimeline;